import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { formatDate } from '@/utils/format';
import { resolveContract } from '@/utils/contract';
import type { ContractStatus } from '@/types/portal';

interface ContractPreviewModalProps {
  open: boolean;
  onClose: () => void;
  creatorName: string;
  handle: string;
  fileName: string;
  fileType: string;
  body: string;
  values: Record<string, string>;
  status: ContractStatus;
  signedAt?: string;
  signedBy?: string;
  signatureRef?: string;
  onDownload: () => void;
}

export default function ContractPreviewModal({
  open,
  onClose,
  creatorName,
  handle,
  fileName,
  fileType,
  body,
  values,
  status,
  signedAt,
  signedBy,
  signatureRef,
  onDownload,
}: ContractPreviewModalProps) {
  const resolved = resolveContract(body, values);
  const signed = status === 'Signed';

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-file-paper-2-line"
      title={fileName}
      subtitle={`${creatorName} · ${handle} · ${fileType}`}
      widthClassName="max-w-3xl"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button variant="outline" size="sm" icon="ri-printer-line" onClick={onClose}>
            Print
          </Button>
          <Button variant="primary" size="sm" icon="ri-file-pdf-2-line" onClick={onDownload}>
            PDF
          </Button>
        </>
      }
    >
      <div className="rounded-lg border border-background-200 bg-background-100 px-5 py-4">
        <p className="mb-3 flex items-center gap-2 text-[11px] font-label uppercase tracking-wider text-foreground-400">
          <span className="w-3.5 h-3.5 flex items-center justify-center text-primary-600">
            <i className="ri-magic-line" />
          </span>
          Dynamic fields resolved for this creator
        </p>
        <pre className="max-h-[46vh] overflow-y-auto whitespace-pre-wrap font-body text-xs leading-relaxed text-foreground-800">
          {resolved}
        </pre>
      </div>

      {signed ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50 text-lg">
            <i className="ri-shield-check-line" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-accent-900">
              Digitally signed by {signedBy ?? creatorName}
            </p>
            <p className="text-xs text-accent-700">
              {signedAt ? `Signed on ${formatDate(signedAt)}` : 'Signature recorded'}
              {signatureRef ? ` · Reference ${signatureRef}` : ''}
            </p>
          </div>
          <Badge tone="accent" icon="ri-check-line">
            Signed
          </Badge>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50 text-lg">
            <i className="ri-lock-2-line" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-secondary-900">Awaiting creator signature</p>
            <p className="text-xs text-secondary-900">
              This creator cannot be fully hired until the contract is signed.
            </p>
          </div>
          <Badge tone="secondary" icon="ri-time-line">
            Unsigned
          </Badge>
        </div>
      )}
    </Modal>
  );
}